import type {
  SearchRequest, SearchResponse,
  ChatbotRequest, ChatbotResponse,
  ConversacionSummary, ConversacionDetail,
} from '../types/api.types';
import { getTokenVigente, irAlPortalDeApps } from '../config/keycloak.config';

export const getApiBaseUrl = (): string => (window as any).__env__?.API_BASE_URL;

/**
 * Petición al backend con el access token de Keycloak.
 *
 * El token se pide fresco en cada llamada, no se guarda: `keycloak-js` lo
 * renueva por detrás y una copia en `localStorage` quedaría vencida sin que
 * nadie se entere. Es también la razón por la que ya no queda nada de sesión
 * en `localStorage` — la sesión vive en Keycloak, no aquí.
 */
async function apiFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const token = await getTokenVigente();

  const response = await fetch(`${getApiBaseUrl()}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers as Record<string, string> ?? {}),
    },
  });

  // Un 401 con un token recién renovado significa que la sesión del realm ya
  // no existe (logout desde otra app, o refresh token vencido). No hay login
  // propio al que volver: la salida es el portal de apps, igual que el logout.
  if (response.status === 401) {
    irAlPortalDeApps();
  }

  return response;
}

export class ApiService {
  static async getConversaciones(): Promise<ConversacionSummary[]> {
    const response = await apiFetch('/api/v1/conversaciones');
    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`);
    }
    return response.json();
  }

  static async getConversacion(id: string): Promise<ConversacionDetail> {
    const response = await apiFetch(`/api/v1/conversaciones/${id}`);
    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`);
    }
    return response.json();
  }

  static async createConversacion(titulo?: string): Promise<ConversacionDetail> {
    const response = await apiFetch('/api/v1/conversaciones', {
      method: 'POST',
      body: JSON.stringify({ titulo: titulo ?? null }),
    });
    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`);
    }
    return response.json();
  }

  static async renameConversacion(id: string, titulo: string): Promise<ConversacionSummary> {
    const response = await apiFetch(`/api/v1/conversaciones/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ titulo }),
    });
    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`);
    }
    return response.json();
  }

  static async deleteConversacion(id: string): Promise<void> {
    const response = await apiFetch(`/api/v1/conversaciones/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok && response.status !== 404) {
      throw new Error(`API Error: ${response.status}`);
    }
  }

  static async search(request: SearchRequest): Promise<SearchResponse> {
    const response = await apiFetch('/api/v1/search', {
      method: 'POST',
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }
    return response.json();
  }

  static async chatbot(request: ChatbotRequest): Promise<ChatbotResponse> {
    const response = await apiFetch('/api/v1/chatbot', {
      method: 'POST',
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }
    return response.json();
  }
}
